import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import * as THREE from 'three'

import EditorFactory from '@glass-designer/utils/editorFactory'

// Base object
// Used by all objects in the THREE.js scene to inherit from
// THREE.js doesn't really use ES6 classes, so this is our lightweight wrapper around the scenegraph
export default class BaseObject {
  constructor(editor) {
    this.id = uuidv4()
    this.editor = editor
    this.sceneObject = null
    this.parentObject = null
    this.children = []
    this.userData = null
    this.visible = true
    this.enabled = true
    this.persistable = true
    this.objectClassName = 'BaseObject'

    this.selected = false

    // If the listener is set, events on the object will be sent to the listener
    this.eventListener = null

    // listenMouseEvents
    // If set to true, this object will receive mouse events
    this.listenMouseEvents = false

    if(editor)
      editor.addSceneObject(this)
  }

  // Dispose of the object & remove it from the scene
  dispose() {
    // Dispose all children
    this.children.forEach(childObj => {
      childObj.dispose()
    })
    this.children = []

    if(this.editor)
      this.editor.removeSceneObject(this)

    if(this.sceneObject) {
      this.editor.scene.remove(this.sceneObject)
      this.sceneObject = null
    }
  }

  // Clear the scene object
  // Removes additional objects, but keeps child objects
  clearSceneObject() {
    // Go through all children & attempt to dispose of all geometry/materials
    this.sceneObject.children.forEach(childObj => {
      if(childObj.geometry)
        childObj.geometry.dispose()
      if(childObj.material)
        childObj.material.dispose()
    })

    this.sceneObject.clear()

    // Re-add our child objects
    this.children.forEach(childObj => {
      if(childObj.getSceneObject() == null) {
        console.log(childObj)
        console.log(this)
      }
      this.sceneObject.add(childObj.getSceneObject())
    })
  }
  

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    this.children.forEach(childObj => {
      childObj.onBeforeRender()
    })
  }

  /**
   * Update the view level on the object
   * Adjusts the scene object matrix & visibility for editing / viewing modes
   */
  viewLevelUpdated(viewLevel) {
  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData) {    
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
  }

  // onMouseEnterEvent
  onMouseEnterEvent(ev, mouseData) {
  }

  // onMouseLeaveEvent
  onMouseLeaveEvent(ev, mouseData) {
  }

  // onMouseWheelEvent
  onMouseWheelEvent(ev, mouseData) {
  }

  setVisible(visible) {
    this.visible = visible
    if(this.sceneObject)
      this.sceneObject.visible = this.visible

    // Update children
    this.children.forEach(childObj => {
      childObj.setVisible(this.visible)
    })
  }
  isVisible() {
    return this.visible
  }
  setEnabled(enabled) {
    this.enabled = enabled
  }
  isEnabled() {
    return this.enabled
  }

  clearSelection() {
    this.setSelected({}, false)
  }

  setSelected(selectionData, isSelected) {
    this.selected = isSelected
  }
  getSelected() {
    return this.selected
  }

  getSceneObject() {
    return this.sceneObject
  }


  addChildObject(obj) {
    if(this.sceneObject)
      this.sceneObject.add(obj.getSceneObject())
      
    obj.parentObject = this
    this.children.push(obj)
  }
  removeChildObject(obj) {
    obj.parentObject = null
    this.children = this.children.filter(c => c.id != obj.id)
  }

  getParentObject() {
    return this.parentObject
  }
  getChildObjectList() {
    return this.children
  }

  childObjectUpdated(childObj) {
  }

  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    return null
  }


  /**
   * Get the object's bounding box as a THREE.Box3 object
   * Includes all THREE.js child objects
   */
  getBoundingBox() {
    if(this.sceneObject == null)
      return null
    return new THREE.Box3().setFromObject(this.sceneObject)
  }

  // Clone the object
  // Should be handled by the inheriting objects
  clone() {
    return null
  }

  /**
   * Copy details from an object to this object
   */
  copy(obj) {
    this.parentObject = obj.parentObject
    this.userData = _.cloneDeep(obj.userData)
  }

  // Serialize/Deserialize to/from a JSON object
  serialize() {
    var outputData = {
      id: this.id,
      userData: _.cloneDeep(this.userData),
      class: this.objectClassName || this.constructor.name
    }

    // Serialize children
    outputData.children = this.children.filter(childObj => childObj.persistable).map(childObj => childObj.serialize())

    return outputData
  }
  deserialize(object) {
    this.id = object.id || this.id
    this.userData = _.cloneDeep(object.userData)

    // Children
    // Handle deletion of child objects when undoing add actions
    var remainderChildList = _.clone(this.children)
    var dataChildList = object.children || []

    dataChildList.forEach(childData => {
      // Find existing child & apply that
      var existingObjIndex = remainderChildList.findIndex(obj => obj.id == childData.id)
      if(existingObjIndex != -1) {
        let existingObj = this.children.find(child => child.id == childData.id)
        if(existingObj)
          existingObj.deserialize(childData)
        
        remainderChildList.splice(existingObjIndex, 1)
        
      } else {
        var newObj = EditorFactory.createObject(childData.class, this.editor)
        if(newObj) {
          this.addChildObject(newObj)
          newObj.deserialize(childData)
        }
      }
    })

    // Handle delete (child created, needs to be undone)
    // Child objects that now exist in the scene but not in the stored list, remove them
    // Don't delete children not flagged as persistable
    remainderChildList.forEach(oldChildObj => {
      if(!oldChildObj.persistable)
        return

      var removeChild = this.children.find(child => child.id == oldChildObj.id)
      this.children = this.children.filter(child => child.id != oldChildObj.id)

      if(removeChild)
        this.editor.removeSceneObject(removeChild)
    })
  }


  /**
   * Find a THREE.Object3D object in the scene object using a userData filter
   * Returns a list of all objects found
   */
  findSceneObjectChildren(userDataFilter) {
    return this.sceneObject.children.filter(childObj => {
      if(childObj.userData == null)
        return false

      let filterKeys = Object.keys(userDataFilter)
      return filterKeys.filter(key => childObj.userData[key] === userDataFilter[key]).length == filterKeys.length
    })
  }


  /**
   * Set the object event listener for this object
   */
  setObjectEventListener(listener) {
    this.eventListener = listener
  }


  /**
   * Send an object event to the listener (if set)
   */
  sendObjectEvent(eventType, data = {}) {
    if(this.eventListener) {
      this.eventListener.onReceiveObjectEvent(this, eventType, data)
    }
  }


}
